import React, { useState, useEffect } from 'react';
import {
  calculateDistance,
  calculateBearing,
  formatDistance,
  watchPosition,
  clearWatch
} from '../../utils/geolocation';

/**
 * TacticalCompass - Shows distance and bearing to target location
 * Integrates with mission-style UI for location-based navigation
 */
export function TacticalCompass({ targetCoordinates, locationName }) {
  const [userPosition, setUserPosition] = useState(null);
  const [distance, setDistance] = useState(null);
  const [bearing, setBearing] = useState(null);
  const [permissionState, setPermissionState] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [error, setError] = useState(null);
  const [watchId, setWatchId] = useState(null);
  const [deviceHeading, setDeviceHeading] = useState(null);
  const [compassEnabled, setCompassEnabled] = useState(false);
  const [compassPermission, setCompassPermission] = useState('unknown'); // 'unknown', 'granted', 'denied'

  // Request location permission and start watching
  const requestLocation = () => {
    setPermissionState('requesting');
    setError(null);

    const id = watchPosition((position) => {
      setUserPosition(position);
      setPermissionState('granted');
      setError(null);
      // Try auto-enabling compass for non-iOS devices
      // iOS will require manual button tap due to permission requirements
      if (typeof DeviceOrientationEvent.requestPermission !== 'function') {
        requestCompass();
      }
    });

    if (id) {
      setWatchId(id);
    } else {
      setPermissionState('denied');
      setError('Geolocation not supported');
    }
  };

  // Request compass/device orientation permission
  const requestCompass = async () => {
    // Check if DeviceOrientationEvent exists
    if (!window.DeviceOrientationEvent) {
      setCompassPermission('denied');
      return;
    }

    // iOS 13+ requires permission request
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          setCompassPermission('granted');
          enableCompass();
        } else {
          setCompassPermission('denied');
        }
      } catch (error) {
        console.error('Compass permission error:', error);
        setCompassPermission('denied');
      }
    } else {
      // Non-iOS or older iOS - compass available without permission
      setCompassPermission('granted');
      enableCompass();
    }
  };

  // Enable compass event listener
  const enableCompass = () => {
    const handleOrientation = (event) => {
      // webkitCompassHeading gives true heading (0-360, 0 = North)
      // event.alpha gives rotation (0-360, but 0 might not be North)
      if (event.webkitCompassHeading !== undefined) {
        // iOS - webkitCompassHeading is already true heading
        setDeviceHeading(event.webkitCompassHeading);
      } else if (event.absolute && event.alpha !== null) {
        // Android/others with absolute orientation
        // Convert alpha to heading (alpha 0 = North, increases clockwise)
        setDeviceHeading(360 - event.alpha);
      } else if (event.alpha !== null) {
        // Fallback - less accurate but better than nothing
        setDeviceHeading(360 - event.alpha);
      }
    };

    // Try absolute orientation first, fall back to regular
    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientationabsolute', handleOrientation, true);
      window.addEventListener('deviceorientation', handleOrientation, true);
      setCompassEnabled(true);
    }

    // Cleanup function
    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  };

  // Calculate distance and bearing when position updates
  useEffect(() => {
    if (userPosition && targetCoordinates) {
      const dist = calculateDistance(
        userPosition.lat,
        userPosition.lng,
        targetCoordinates.lat,
        targetCoordinates.lng
      );
      const bear = calculateBearing(
        userPosition.lat,
        userPosition.lng,
        targetCoordinates.lat,
        targetCoordinates.lng
      );
      setDistance(dist);
      setBearing(bear);
    }
  }, [userPosition, targetCoordinates]);

  // Cleanup watch on unmount
  useEffect(() => {
    return () => {
      if (watchId) {
        clearWatch(watchId);
      }
    };
  }, [watchId]);

  // Generate Google Maps URL for fallback
  const mapsUrl = targetCoordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${targetCoordinates.lat},${targetCoordinates.lng}`
    : null;

  // If no target coordinates, don't render anything
  if (!targetCoordinates) {
    return null;
  }

  // Permission prompt state
  if (permissionState === 'prompt') {
    return (
      <div
        className="rounded-xl p-4"
        style={{
          background: 'rgba(71, 85, 105, 0.3)',
          border: '1px solid rgba(71, 85, 105, 0.5)'
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'rgba(16, 185, 129, 0.2)',
              border: '1px solid rgba(16, 185, 129, 0.4)'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="#10B981" strokeWidth="2" />
              <path d="M12 3 L14 9 L12 8 L10 9 Z" fill="#10B981" />
              <path d="M12 21 L10 15 L12 16 L14 15 Z" fill="#64748b" opacity="0.5" />
              <circle cx="12" cy="12" r="2" fill="#10B981" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm font-semibold text-white">
                In App Navigation
              </div>
              <div
                className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                style={{
                  background: '#FCD34D',
                  color: '#78350F'
                }}
              >
                Beta
              </div>
            </div>
            <div className="text-xs text-gray-400">
              Enable location to see distance and bearing to target
            </div>
          </div>
          <button
            onClick={requestLocation}
            className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              color: '#1e293b',
              border: 'none'
            }}
          >
            Enable
          </button>
        </div>
      </div>
    );
  }

  // Requesting permission state
  if (permissionState === 'requesting') {
    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)'
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <div className="text-sm text-gray-300">Acquiring GPS signal...</div>
        </div>
      </div>
    );
  }

  // Permission denied state with fallback to maps
  if (permissionState === 'denied' || error) {
    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.4)'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-1">
              Location Unavailable
            </div>
            <div className="text-xs text-gray-300">Use map navigation instead</div>
          </div>
          {mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg text-sm font-semibold uppercase tracking-wide whitespace-nowrap"
              style={{
                background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
                color: '#ffffff',
                border: 'none',
                textDecoration: 'none'
              }}
            >
              Navigate
            </a>
          )}
        </div>
      </div>
    );
  }

  // Calculate relative angle for compass arrow
  const getArrowRotation = () => {
    if (bearing === null) return 0;
    if (deviceHeading === null || !compassEnabled) {
      // No compass - return bearing directly for static arrow
      return bearing;
    }
    // Calculate relative angle: where to point relative to device orientation
    let relativeAngle = bearing - deviceHeading;
    // Normalize to -180 to 180 range
    while (relativeAngle > 180) relativeAngle -= 360;
    while (relativeAngle < -180) relativeAngle += 360;
    return relativeAngle;
  };

  // Active tracking state with distance and bearing
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: 'rgba(71, 85, 105, 0.3)',
        border: '1px solid rgba(71, 85, 105, 0.5)'
      }}
    >
      {/* Main compass display */}
      <div className="p-4">
        <div className="flex items-center gap-4">
          {/* Large directional arrow - ONLY show when compass is active */}
          {compassEnabled && deviceHeading !== null && (
            <div
              className="w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 relative"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}
            >
              {/* Dynamic compass arrow - rotates with device */}
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                style={{
                  transform: `rotate(${getArrowRotation()}deg)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <path
                  d="M12 2 L16 10 L12 8 L8 10 Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="0.5"
                />
                <circle cx="12" cy="12" r="1.5" fill="white" opacity="0.5" />
              </svg>
              {/* Compass indicator badge */}
              <div
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(16, 185, 129, 0.9)',
                  border: '2px solid rgba(71, 85, 105, 0.8)',
                  fontSize: '10px'
                }}
                title="Live compass active"
              >
                🧭
              </div>
            </div>
          )}

          {/* Distance and direction info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm font-semibold text-white">
                In App Navigation
              </div>
              <div
                className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                style={{
                  background: '#FCD34D',
                  color: '#78350F'
                }}
              >
                Beta
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <div className="text-xl font-bold font-mono text-white">
                {distance !== null ? formatDistance(distance) : '---'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accuracy and compass status indicator */}
      <div
        className="px-4 py-2 border-t"
        style={{
          background: 'rgba(0, 0, 0, 0.2)',
          borderColor: 'rgba(16, 185, 129, 0.2)'
        }}
      >
        <div className="flex items-center justify-between gap-4">
          {/* GPS Accuracy */}
          {userPosition?.accuracy && (
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  userPosition.accuracy < 20
                    ? 'bg-emerald-400'
                    : userPosition.accuracy < 50
                    ? 'bg-amber-400'
                    : 'bg-red-400'
                }`}
              />
              <div className="text-xs text-gray-400">
                GPS: ±{Math.round(userPosition.accuracy)}m
              </div>
            </div>
          )}

          {/* Compass Status */}
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                compassEnabled && deviceHeading !== null
                  ? 'bg-emerald-400'
                  : compassPermission === 'denied'
                  ? 'bg-red-400'
                  : 'bg-gray-500'
              }`}
            />
            <div className="text-xs text-gray-400">
              {compassEnabled && deviceHeading !== null
                ? `Compass: ${Math.round(deviceHeading)}°`
                : compassPermission === 'denied'
                ? 'Compass: Denied'
                : 'Compass: Off'}
            </div>
          </div>
        </div>

        {/* Enable Compass Button - Show if compass not enabled and iOS device */}
        {!compassEnabled && typeof DeviceOrientationEvent.requestPermission === 'function' && (
          <button
            onClick={requestCompass}
            className="mt-3 w-full py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none'
            }}
          >
            🧭 Enable Live Compass
          </button>
        )}

        {/* Compass calibration tip - show if enabled but might need calibration */}
        {compassEnabled && deviceHeading === null && (
          <div className="mt-2 text-xs text-amber-400">
            🧭 Move device in figure-8 pattern to calibrate compass
          </div>
        )}
      </div>
    </div>
  );
}
